import React from "react";
import styled from "styled-components";

import {
  // Removed while the "Assessments in Progress" box is temporarily hidden.
  // filterMaybeArray,
  getTagFromResource,
} from "@examspark/common";
import { useAppContext } from "@examspark/common-app";
import { Callout, Text } from "@examspark/common-ui";
import { CardLink } from "@examspark/common-ui/dist/components/containers/LinkCard";
import {
  // Removed while the "Assessments in Progress" box is temporarily hidden.
  // IconAtomLine,
  IconBookLine,
  IconClockLine,
} from "@examspark/common-ui/dist/components/icons";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import {
  ObjectType,
  ParsedSearchableResourceConnectionNew,
  Resource,
  UserClassroom,
} from "@examspark/types";

import { useFlags } from "../../hooks/useFlags";
import { serializeBrowseFilter } from "../browse/Browse";
// Removed while the "Assessments in Progress" box is temporarily hidden.
// import ActivitiesList from "../classroom/sidebar/ActivitiesList";
import { SidebarLinkItem } from "../classroom/sidebar/BasicSidebarListItem";
import Copyright from "../common/Copyright";
import calendar from "../common/graphics/calendar.svg";
import checklist from "../common/graphics/checklist.svg";
import location from "../common/graphics/location.svg";
import sun from "../common/graphics/sun.svg";
import SidebarBox, { EmptySidebarBox } from "../common/SidebarBox";

import AdBanner from "./AdBanner";
import { CampfireAssessmentBanner } from "./campfire-assessment-banner/CampfireAssessmentBanner";
import DashboardBrowser from "./DashboardBrowser";
import DashboardFavorites from "./DashboardFavorites";
import DashboardPlanner from "./DashboardPlanner";
import DashboardSections from "./DashboardSections";

export const iconOf = (icon: string) => {
  switch (icon) {
    case "sun":
      return sun;
    case "location":
      return location;
    case "calendar":
      return calendar;
    case "checklist":
      return checklist;
    default:
      return;
  }
};

const Styled = styled.div`
  width: 100%;
  max-width: var(--app-contain-width);

  margin: 0 auto;
  /* display: flex; */
  gap: 3rem;
  display: grid;
  grid-template-areas:
    "main sidebar"
    "footer sidebar";

  grid-template-columns: 1fr clamp(30ch, 30vw, 30rem);
  grid-template-rows: auto 1fr;

  @media (max-width: 600px) {
    & {
      grid-template-areas:
        "main"
        "sidebar"
        "footer";
      grid-template-columns: 1fr;
    }
  }
`;

const StyledCallout = styled(Callout)`
  font-size: 1.5rem;
`;

const LayoutMain = styled.main`
  flex: 1;
  grid-area: main;
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;
const LayoutSidebar = styled.aside`
  grid-area: sidebar;

  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const SectionTitle = styled(Text.H1)`
  font-weight: 500;

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;
export const SectionGrid = styled.div`
  gap: 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(26rem, 1fr));
`;

const Footer = styled.footer`
  border-top: solid 1px ${colorFromTheme("surface5")};
  font-size: var(--font-size-xsmall);
  line-height: 1.5em;
  padding-top: 1rem;
  margin-top: 2rem;
  color: ${colorFromTheme("secondary", "textColor")};
  grid-area: footer;
`;

export const EmptyBox = styled.div`
  padding: 5rem;
  text-align: center;
  border-radius: 0.5rem;
  border: dashed 2px hsl(0, 0%, 85%, 1);
`;
type SidebarItemInfo = {
  id: string;
  title: string;
  type: string;
  icon: string;
};
type DashboardProps = {
  sections?: UserClassroom[];
  favorites?: Resource[];
  recents: SidebarItemInfo[];
  activities?: ParsedSearchableResourceConnectionNew;
  onCreate: () => void;
};

const defaultFilter = {
  grade: [],
  type: ["COURSE" as ObjectType],
};

export const defaultBrowseFiltersBySubject = [
  "MATH",
  "SCI",
  "ELA",
  "SLA",
  "SS",
].reduce(
  (acc, subject) => ({
    ...acc,
    [subject]: serializeBrowseFilter({
      ...defaultFilter,
      subject,
    }),
  }),
  {} as Record<string, string>,
);

defaultBrowseFiltersBySubject.PK = serializeBrowseFilter({
  ...defaultFilter,
  grade: ["GRADE_PK"],
});

const Dashboard = ({ sections, favorites, onCreate }: DashboardProps) => {
  const [context] = useAppContext();
  const flags = useFlags();

  const isWorkspaceSuspended = !!getTagFromResource(
    context.appLicense,
    "tcmpc-suspended",
  )?.value;

  return (
    <Styled>
      <LayoutMain>
        {isWorkspaceSuspended && (
          <StyledCallout indicatorColor="danger" backgroundColor="surface2">
            This workspace needs attention: The Administrator of this workspace
            should contact TCMPC to ensure continued access to TCMPC Curriculum
            resources.
          </StyledCallout>
        )}
        <DashboardSections sections={sections} onCreate={onCreate} />
        <AdBanner endAt={new Date(2025, 5, 7)} />
        <DashboardBrowser />
      </LayoutMain>
      <LayoutSidebar>
        <SidebarBox
          title="System Links"
          footer={
            <SidebarBox.FooterButtons>
              <SidebarLinkItem>
                <CardLink to="/workspace/exams">Assessments</CardLink>
              </SidebarLinkItem>
              <SidebarLinkItem>
                <CardLink to="/glossary">Glossary</CardLink>
              </SidebarLinkItem>
              <SidebarLinkItem>
                <CardLink to={`/revisions`}>Revision Notes</CardLink>
              </SidebarLinkItem>
              <SidebarLinkItem>
                <CardLink to={`/browse`}>Browse All Content</CardLink>
              </SidebarLinkItem>
            </SidebarBox.FooterButtons>
          }
        />
        <DashboardPlanner />
        {/* Hiding confusing UI until digital assessments make their glorious come back.
          <SidebarBox title="Assessments in Progress">
          {!activities?.items?.length && (
            <EmptySidebarBox>
              <IconAtomLine />
              <div>You do not have any Assessments in progress.</div>
            </EmptySidebarBox>
          )}
          {!!activities?.items?.length && (
            <ActivitiesList
              activities={filterMaybeArray(activities?.items ?? [])}
            />
          )}
        </SidebarBox> */}
        {flags.tcmpcAssessmentAppShown && (
          <CampfireAssessmentBanner
            disabled={!flags.tcmpcAssessmentAppEnabled}
          />
        )}
        <DashboardFavorites favorites={favorites} />
        <SidebarBox title="Recents">
          <EmptySidebarBox>
            <IconClockLine />
            <div>You don't have any recents yet.</div>
          </EmptySidebarBox>
        </SidebarBox>
        <SidebarBox title="Resources">
          <EmptySidebarBox>
            <IconBookLine />
            <div>You don't have any resources yet.</div>
          </EmptySidebarBox>
        </SidebarBox>
      </LayoutSidebar>
      <Footer>
        <Copyright />
      </Footer>
    </Styled>
  );
};

export default Dashboard;
